<template>
  <div>
    <div v-if="props.objectType === 'tasks'"
         class="filter-buttons"
    >
      <button v-for="status in [0, 1, 2, 3, 4, 5]"
              :key="status"
              :class="{ active: selectedStatuses.has(status) }"
              @click="toggleStatusFilter(status)"
      >
        {{ statusLabels[status] }}
      </button>
    </div>

    <table class="styled-table">
      <thead v-if="showHeader">
        <tr>
          <th :colspan="tableColumns.length"
              :style="{ padding: showHeader ? '8px 15px' : '12px 15px' }"
              @click="toggleTaskTable"
          >
            {{ isTaskTableVisible ? '▼' : '▲' }} {{ headerText }}
          </th>
        </tr>
      </thead>

      <thead>
        <tr>
          <th v-for="column in tableColumns"
              :key="column"
              :style="{ padding: showHeader ? '8px 15px' : '12px 15px' }"
              @click="sortTable(column)"
          >
            {{ $t(`taskPage.${column.toLowerCase()}`) }}
            <span v-if="sortColumn === column">
              {{ sortOrder === 'asc' ? '▲' : '▼' }}
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-if="props.loading">
          <tr>
            <td :colspan="tableColumns.length || 3"
                class="loading-cell"
            >
              <div class="spinner" />
            </td>
          </tr>
        </template>
        <template v-else>
          <tr v-for="task in filteredAndSortedTasks"
              v-show="isTaskTableVisible"
              :key="task.Id"
              class="taskRow"
              :class="{
                'priority-high': ['High', 'Akut', 'Hög'].includes(task.Priority),
                'priority-low': ['Låg', 'Low', 'Lav'].includes(task.Priority)
              }"
              @click="openTask(task)"
          >
            <td v-for="column in tableColumns"
                :key="column"
                :class="`tdTask ${task.Unread && task.Unread > 0 ? 'unreadRow' : ''}`"
            >
              {{ getColumnValue(task, column) }}
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { defineProps, ref, computed, defineEmits } from 'vue'
import router from '@/router'
import i18n from '../../locales/i18n'

const props = defineProps({
  tasks: {
    type: Array,
    default () {
      return []
    }
  },
  showHeader: {
    type: Boolean,
    default: false
  },
  headerText: {
    type: String,
    default: ''
  },
  tableColumns: {
    type: Array,
    default () {
      return ['Nr', 'PlannedDate', 'Customer'] // default columns
    }
  },
  redirect: {
    type: Boolean,
    default: true
  },
  objectType: {
    type: String,
    default: 'tasks'
  },
  loading: {
    type: Boolean,
    default: false
  }
})
const sortColumn = ref(null)
const sortOrder = ref('asc')

const selectedStatuses = ref(new Set(props.selectedStatuses || [0, 1, 2, 3, 4, 5]))

const toggleStatusFilter = (status) => {
  if (selectedStatuses.value.has(status)) {
    selectedStatuses.value.delete(status)
  } else {
    selectedStatuses.value.add(status)
  }
  emit('statusChanged', Array.from(selectedStatuses.value))
}
const statusLabels = computed(() => ({
  0: i18n.global.t('taskPage.status0'),
  1: i18n.global.t('taskPage.status1'),
  2: i18n.global.t('taskPage.status2'),
  3: i18n.global.t('taskPage.status3'),
  4: i18n.global.t('taskPage.status4'),
  5: i18n.global.t('taskPage.status5')
}))
const emit = defineEmits(['sortChanged', 'statusChanged'])

const sortTable = (column) => {
  if (sortColumn.value === column) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortColumn.value = column
    sortOrder.value = 'asc'
  }
  emit('sortChanged', { column: sortColumn.value, order: sortOrder.value })
}

const filteredAndSortedTasks = computed(() => {
  const tasks = [...props.tasks]

  // Apply status filter only if objectType is "tasks"
  if (props.objectType !== 'tasks') {
    if (sortColumn.value) {
      tasks.sort((a, b) => {
        const valueA = getColumnValue(a, sortColumn.value)
        const valueB = getColumnValue(b, sortColumn.value)
        if (valueA === '' || valueA === null || valueA === undefined) return 1
        if (valueB === '' || valueB === null || valueB === undefined) return -1
        if (valueA < valueB) return sortOrder.value === 'asc' ? -1 : 1
        if (valueA > valueB) return sortOrder.value === 'asc' ? 1 : -1
        return 0
      })
    }
  }

  return tasks
})
const eligiblePriorities = computed(() => ({
  Low: i18n.global.t('taskPage.priorityLow'),
  Normal: i18n.global.t('taskPage.priorityNormal'),
  High: i18n.global.t('taskPage.priorityHigh')
}))

// The Mobigo Priority should exist as a key in below dict
const priorityMapping = {
  UK: { Low: 'Low', Normal: 'Normal', High: 'High' },
  SE: { Låg: 'Low', Normal: 'Normal', Akut: 'High', Hög: 'High' },
  DK: { Lav: 'Low', Normal: 'Normal', Akut: 'High' },
  NO: { Låg: 'Low', Normal: 'Normal', High: 'High' },
  NL: { Low: 'Low', Normal: 'Normal', High: 'High' }
}

const currentEnv = window.apiConfig.ENV || 'UK'
const getBackendPriority = (priority) => {
  if (!priority) {
    return eligiblePriorities.value.Normal // If no priority, default to 'Normal'
  }

  // Find the appropriate mapping for the current environment (e.g., SE, UK)
  const environmentMapping = priorityMapping[currentEnv] || priorityMapping.value.UK

  // Map the task priority to the common 'Low', 'Normal', 'High' keys
  const mappedPriority = environmentMapping[priority] || 'Normal' // Default to 'Normal'

  // Return the translated priority from eligiblePriorities
  return eligiblePriorities.value[mappedPriority] || eligiblePriorities.value.Normal
}
const isTaskTableVisible = ref(true)
const toggleTaskTable = () => {
  isTaskTableVisible.value = !isTaskTableVisible.value
}

function openTask (task) {
  if (props.redirect) {
    // If we come from message view, the task.Id is the message id, but we want to redirect to TaskId.
    if (props.objectType === 'messages') {
      router.push('/tasks/' + task.TaskId)
    } else {
      router.push('/' + props.objectType + '/' + task.Id)
    }
  }
}

function getColumnValue (task, column) {
  function isValidDate (dateString) {
    // Check if the date is valid and greater than 1970
    const date = new Date(dateString)
    return !isNaN(date.getTime()) && date.getFullYear() > 1970
  }
  if (column === 'PlannedDate') {
    return task.StartDate.substring(0, 10) +
      (new Date(task.StopDate).setDate(new Date(task.StopDate).getDate() - 1) >
        new Date(task.StartDate)
        ? ' - ' + task.StopDate.substring(0, 10)
        : '')
  } else if (column === 'Customer') {
    return task.Customer?.Name
  } else if (column === 'Priority') {
    return getBackendPriority(task.Priority)
  } else if (column === 'TaskDescription') {
    return task.UserProperties[10500]?.Value
  } else if (column === 'Time' || column === 'Amount') {
    return task[column] + ' ' + task.Unit
  } else if (column === 'StartDate' || column === 'FinishedDate' || column === 'CreatedDate') {
    const dateValue = task[column].substring(0, 10)
    return isValidDate(dateValue) ? dateValue : ''
  } else if (column === 'Status') {
    return i18n.global.t('taskPage.status' + task.Status)
  } else if (column === 'Contract' && task.Contract) {
    if (task.Contract.UserProperties && task.Contract.UserProperties.Avtalsbeskrivning && task.Contract.UserProperties.Avtalsbeskrivning !== '') {
      return task.Contract.UserProperties.Avtalsbeskrivning
    } else if (task.Contract.UserProperties && task.Contract.UserProperties['Contract description']) {
      return task.Contract.UserProperties['Contract description']
    } else {
      return task.Contract.Nr
    }
  } else if (column === 'Object1') {
    return task.Object1?.Name
  } else if (column === 'LatestMessageUser') {
    return task.Sender
  } else if (column === 'LatestMessageMessage') {
    return task.Message
  } else if (column === 'LatestMessageTime') {
    return task.Created.substring(0, 10)
  } else if (column === 'Performers') {
    return task.Performers.map(performer => performer.Name).join(', ')
  } else if (column === 'Owner') {
    return task.Owner?.Name
  } else {
    return task[column]
  }
}

// 'LatestMessageUser', 'LatestMessageMessage', 'LatestMessageTime'
</script>

<style lang="scss" scoped>
.loading-cell {
  padding: 40px 0;
  background-color: #f0f0f0;
  text-align: center;
  width: 100%;
}
.table-wrapper {
  position: relative;
  width: 100%;
  min-height: 200px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  width: 100%;
  height: 100%;
  background-color: #f0f0f0cc; // light grey w/ slight transparency
  display: flex;
  justify-content: center;
  align-items: center;
}
.loading-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.spinner {
  border: 5px solid #f3f3f3;
  border-top: 5px solid var(--mainBlue);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
   margin: auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.taskRow{
  border-bottom: 1px solid lightgrey;
  background-color: white;
  color: black;
  height: 20px;

  &:hover {
    cursor: pointer;
  }

  .header{
    font-weight: bold;
  }
}

.taskRow:nth-of-type(2n) {
  background-color: var(--alternatingListColor);
  color: black;
}

.taskRow:last-of-type {
  border-bottom: 3px solid var(--mainBlue);
}

.tdTask {
  padding: 10px 16px;
  text-align: left;
  transition: padding 0.4s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.filter-buttons button {
  float:left;
  padding: 5px 10px;
  cursor: pointer;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
  color: #333;
  border-radius: 5px;
  transition: background-color 0.3s ease;
}

.filter-buttons button.active {
  background-color: var(--mainBlue);
  color: white;
}
.styled-table {
  table-layout: fixed;
  border-collapse: collapse;
  margin: 5px 0;
  font-size: 0.9em;
  font-family: 'Lexend', sans-serif;
  width: 100%;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
}

.styled-table thead tr {
  background-color: var(--mainBlue);
  color: white;
  text-align: left;
}

.styled-table th {
  padding: 12px 15px;
  text-align: left;
  cursor: pointer;

}

  @media screen and (max-width: 1260px) {
  .styled-table th {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.styled-table tbody tr {
  border-bottom: 1px solid lightgrey;
  background-color: white;
  color: black;
}

.styled-table tbody tr:nth-of-type(even) {
  background-color: #c6e3f2;
}

.styled-table tbody tr:last-of-type {
  border-bottom: 3px solid var(--mainBlue);
}

.table-row:hover {
  background-color: #f5f5f5;
}
.unreadRow {
  font-weight: bold;
}
.taskRow.priority-high {
  background-color: red !important;
  color:black;
}

.taskRow.priority-high:nth-of-type(even) {
  background-color: red;
    color: black;
}

.taskRow.priority-low {
  background-color: rgb(189, 234, 248)  !important;

  color: black;
}

.taskRow.priority-high:nth-of-type(even) {
  background-color: rgb(189, 234, 248);

  color: black;
}
</style>
